import React from 'react';
import clsx from 'clsx';

type ContainerSize = 'content' | 'wide' | 'full';

interface AppShellProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

interface PageProps {
  children: React.ReactNode;
  className?: string;
  backgroundClassName?: string;
  containerSize?: ContainerSize;
  contentClassName?: string;
}

interface ContainerProps {
  children: React.ReactNode;
  size?: ContainerSize;
  className?: string;
}

interface SectionProps {
  children: React.ReactNode;
  className?: string;
}

interface StackProps {
  children: React.ReactNode;
  className?: string;
  space?: 'sm' | 'md' | 'lg' | 'xl';
}

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  aside?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

const containerSizeClasses: Record<ContainerSize, string> = {
  content: 'mx-auto w-full max-w-7xl',
  wide: 'mx-auto w-full max-w-rail',
  full: 'w-full',
};

const stackSpaceClasses: Record<NonNullable<StackProps['space']>, string> = {
  sm: 'space-y-3',
  md: 'space-y-4',
  lg: 'space-y-6',
  xl: 'space-y-8',
};

export const AppShell: React.FC<AppShellProps> = ({ children, className, contentClassName }) => {
  return (
    <div
      className={clsx(
        'flex min-h-[100dvh] flex-col bg-agentic-white-off pt-[env(safe-area-inset-top)]',
        className
      )}
    >
      <div
        className={clsx(
          'flex min-w-0 flex-1 flex-col pb-[max(env(safe-area-inset-bottom),0rem)]',
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

export const Container: React.FC<ContainerProps> = ({ children, size = 'content', className }) => {
  return (
    <div
      className={clsx(
        'min-w-0 px-4 sm:px-6 lg:px-8',
        containerSizeClasses[size],
        size === 'full' && 'px-0 sm:px-0 lg:px-0',
        className
      )}
    >
      {children}
    </div>
  );
};

export const Section: React.FC<SectionProps> = ({ children, className }) => {
  return <section className={clsx('py-6 sm:py-8', className)}>{children}</section>;
};

export const Stack: React.FC<StackProps> = ({ children, className, space = 'md' }) => {
  return <div className={clsx(stackSpaceClasses[space], className)}>{children}</div>;
};

export const Page: React.FC<PageProps> = ({
  children,
  className,
  backgroundClassName,
  containerSize = 'content',
  contentClassName,
}) => {
  return (
    <div className={clsx('min-w-0', backgroundClassName, className)}>
      <Container size={containerSize} className={contentClassName}>
        {children}
      </Container>
    </div>
  );
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  aside,
  className,
  titleClassName,
  subtitleClassName,
}) => {
  return (
    <header className={clsx('grid gap-6 py-8 md:py-10 lg:grid-cols-[1fr_auto] lg:items-end', className)}>
      <div className="min-w-0">
        <h1 className={clsx('min-w-0 truncate text-display-lg text-kv-agentic-body md:text-display-xl', titleClassName)}>
          {title}
        </h1>
        {subtitle ? (
          <p className={clsx('page-subtitle', subtitleClassName)}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {aside ? <div className="min-w-0">{aside}</div> : null}
    </header>
  );
};
